import React from 'react';
import { StudentData, HeaderSettings, ReportTemplate } from './types';
import { generatePdfHeader } from './pdfHeaderGenerator';
import { getCBCLevel, CBC_LEVELS, CORE_COMPETENCIES, VALUES } from './cbcHelpers';
import { School } from 'lucide-react';
import { reportStyles } from './reportStyles'; // Fixed import path

interface CBCReportTemplateProps {
  data: StudentData;
  activeHeaderSettings: HeaderSettings | undefined;
  id?: string;
  gapAdjust?: number;
  template?: ReportTemplate;
}

export const CBCReportTemplate: React.FC<CBCReportTemplateProps> = ({ 
    data, 
    activeHeaderSettings, 
    id = "printable-report", 
    gapAdjust = -80,
    template = 'default' 
}) => {
  const shouldUseCustomHeader = activeHeaderSettings?.isEnabled;
  const styles = reportStyles[template] || reportStyles.default;

  return (
    <div id={id} className={`w-full max-w-[210mm] mx-auto !p-6 print:!p-6 shadow-2xl print:shadow-none print:w-full print:max-w-none flex flex-col text-xs print:h-auto print:min-h-0 ${styles.container} ${styles.font}`}>
      {/* Header */}
      {shouldUseCustomHeader && activeHeaderSettings ? (
        <div dangerouslySetInnerHTML={{ __html: generatePdfHeader(activeHeaderSettings) }} />
      ) : (
        <div className={`!mb-2 !pb-2 flex flex-col items-center justify-center text-center ${styles.headerWrapper}`}>
           <div className={`flex justify-center mb-2 ${styles.schoolTitle.includes('text-white') ? 'text-white' : 'text-slate-800'}`}>
               <School size={32} />
           </div>
           <h1 className={`${styles.schoolTitle} !text-2xl text-center`}>{data.schoolName}</h1>
           <p className={`${styles.subTitle} text-center`}>Learner Progress Report (CBC)</p>
        </div>
      )}

      {/* Learner Identification - Condensed */}
      <div 
        className={`grid !grid-cols-4 gap-4 !mb-2 pb-2 ${styles.infoCard}`}
        style={{ marginTop: `${gapAdjust}px` }}
      >
          <div>
              <div className={styles.infoLabel}>Learner Name</div>
              <div className={`truncate uppercase ${styles.infoValue}`}>{data.name}</div>
          </div>
          <div>
              <div className={styles.infoLabel}>UPI / Assess No.</div>
              <div className={`uppercase ${styles.infoValue}`}>{data.studentId}</div>
          </div>
          <div>
              <div className={styles.infoLabel}>Grade & Stream</div>
              <div className={`uppercase ${styles.infoValue}`}>{data.gradeLevel} - {data.stream}</div>
          </div>
          <div>
               <div className={styles.infoLabel}>Term / Year</div>
               <div className={`uppercase ${styles.infoValue}`}>{data.term} | {data.academicYear}</div>
          </div>
      </div>

      {/* Performance Key - Horizontal Bar */}
      <div className={`!mb-2 flex items-center justify-between p-2 rounded border ${styles.infoCard}`}>
          <span className={`text-[10px] font-bold uppercase mr-2 ${styles.infoLabel}`}>Key:</span>
          <div className="flex gap-4 text-[10px]">
              {CBC_LEVELS.map(level => (
                  <span key={level.code} className={`${level.color} whitespace-nowrap`}>
                      {level.code}: {level.name}
                  </span>
              ))}
          </div>
      </div>

      {/* Assessment Grid - Ultra Compact */}
      <div className={`!mb-2 ${styles.tableContainer}`}>
          <table className="w-full border-collapse text-[10px]">
              <thead>
                  <tr className={styles.tableHead}>
                      <th className={`p-1.5 text-left w-1/3 ${styles.tableHeadText}`}>Learning Area</th>
                      <th className={`p-1 text-center w-8 ${styles.tableHeadText}`}>EE</th>
                      <th className={`p-1 text-center w-8 ${styles.tableHeadText}`}>ME</th>
                      <th className={`p-1 text-center w-8 ${styles.tableHeadText}`}>AE</th>
                      <th className={`p-1 text-center w-8 ${styles.tableHeadText}`}>BE</th>
                      <th className={`p-1.5 text-left ${styles.tableHeadText}`}>Teacher's Remarks</th>
                  </tr>
              </thead>
              <tbody>
                  {data.subjects.map((subject, idx) => {
                      const level = getCBCLevel(subject.score, subject.total);
                      const isEven = idx % 2 === 0;
                      return (
                          <tr key={subject.id} className={isEven ? styles.tableRow : `${styles.tableRow} ${styles.tableRowAlt}`}>
                              <td className="p-1.5 font-semibold border-r border-slate-200/50">
                                  {subject.name}
                              </td>
                              <td className="p-1 text-center border-r border-slate-200/50">
                                  {level.code === 'EE' && <span className="text-green-600 font-bold text-xs">✓</span>}
                              </td>
                              <td className="p-1 text-center border-r border-slate-200/50">
                                  {level.code === 'ME' && <span className="text-blue-600 font-bold text-xs">✓</span>}
                              </td>
                              <td className="p-1 text-center border-r border-slate-200/50">
                                  {level.code === 'AE' && <span className="text-yellow-600 font-bold text-xs">✓</span>}
                              </td>
                              <td className="p-1 text-center border-r border-slate-200/50">
                                  {level.code === 'BE' && <span className="text-red-600 font-bold text-xs">✓</span>}
                              </td>
                              <td className="p-1.5 italic truncate max-w-[200px]">
                                  {subject.teacherRemarks || level.description}
                              </td>
                          </tr>
                      );
                  })}
              </tbody>
          </table>
      </div>

      <div className="grid grid-cols-2 gap-6 !mb-2">
          {/* Core Competencies */}
          <div className={styles.aiSection}>
               <h3 className={styles.sectionTitle}>Core Competencies</h3>
               <ul className="text-[10px] space-y-1 mt-2">
                   {CORE_COMPETENCIES.map((comp, idx) => (
                       <li key={idx} className="flex justify-between items-center border-b border-gray-200/50 pb-1 last:border-0">
                           <span>{comp}</span>
                           <span className="opacity-60 text-[9px]">Observed</span>
                       </li>
                   ))}
               </ul>
          </div>

          {/* Values */}
          <div className={styles.aiSection}>
              <h3 className={styles.sectionTitle}>Values</h3>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] mt-2">
                    {VALUES.map((value, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                           <div className="w-2 h-2 border border-current opacity-60 rounded-sm"></div>
                           <span>{value}</span>
                       </div>
                    ))}
                </div>
          </div>
      </div>

      {/* Footer / Comments - Compact */}
      <div className={`!mt-auto pt-2 border-t ${styles.footer.includes('border') ? '' : 'border-slate-800'}`}>
          <div className={`mb-2 ${styles.remarksSection}`}>
              <label className={styles.infoLabel}>Class Teacher Remarks</label>
              <div className="border-b border-gray-400 border-dashed h-4 mb-1 opacity-50"></div>
              <div className="border-b border-gray-400 border-dashed h-4 opacity-50"></div>
          </div>

          <div className={`mb-4 ${styles.remarksSection}`}>
              <label className={styles.infoLabel}>Head Teacher Remarks</label>
               <div className="text-[10px] italic p-1.5 min-h-[30px]">
                   {data.principalRemark || "No remarks."}
               </div>
          </div>

          <div className={`${styles.footer} !mt-2 !pt-2 !grid-cols-3`}>
              <div>
                   <div className="border-b border-current mb-1 h-6 opacity-30"></div>
                   <div className="text-[9px] uppercase font-bold opacity-70">Class Teacher Sign</div>
              </div>
              <div>
                   <div className="border-b border-current mb-1 h-6 opacity-30"></div>
                   <div className="text-[9px] uppercase font-bold opacity-70">Head Teacher Sign</div>
              </div>
              <div>
                   <div className="border-b border-current mb-1 h-6 opacity-30"></div>
                   <div className="text-[9px] uppercase font-bold opacity-70">Closing Date</div>
              </div>
          </div>
    </div>
    </div>
  );
};
